import { Component, EventEmitter, Input, Output, inject, signal, OnChanges, SimpleChanges, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { LucideAngularModule, MapPin, Calendar, CheckCircle, Euro, Heart, Users, TreePine, Droplets, Home, Briefcase, Leaf, Shield, Globe, Award, LucideIconData, X, ChevronLeft, ChevronRight } from 'lucide-angular';

import { Project, CoBenefit } from '../../../../../admin/models/project.model';

@Component({
  selector: 'app-project-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    DialogModule,
    ButtonModule,
    TagModule,
    DividerModule,
    LucideAngularModule,
  ],
  templateUrl: './project-detail-modal.component.html',
})
export class ProjectDetailModalComponent implements OnChanges {
  @Input() project: Project | null = null;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() selectProject = new EventEmitter<Project>();

  private transloco = inject(TranslocoService);

  // Carousel state
  currentImageIndex = signal(0);
  slideDirection = signal<'left' | 'right' | null>(null);

  ngOnChanges(changes: SimpleChanges): void {
    // Reset carousel to first image when project changes or modal opens
    if (changes['project'] || (changes['visible'] && changes['visible'].currentValue === true)) {
      this.currentImageIndex.set(0);
      this.slideDirection.set(null); // No animation on first load
    }
  }

  private triggerSlide(direction: 'left' | 'right'): void {
    this.slideDirection.set(null);
    // Force a reflow to restart the animation
    setTimeout(() => this.slideDirection.set(direction), 10);
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.visible) {
      this.onClose();
    }
  }

  // Get all available project images (method instead of computed for @Input reactivity)
  getProjectImages(): string[] {
    const images: string[] = [];
    if (this.project?.mainImageUrl) images.push(this.project.mainImageUrl);
    if (this.project?.certificateImageUrl) images.push(this.project.certificateImageUrl);
    if (this.project?.socialShareImageUrl) images.push(this.project.socialShareImageUrl);
    return images;
  }

  // Get current image to display
  getCurrentImage(): string | null {
    const images = this.getProjectImages();
    const index = this.currentImageIndex();
    return images[index] || null;
  }

  // Icons
  mapPinIcon = MapPin;
  calendarIcon = Calendar;
  checkCircleIcon = CheckCircle;
  euroIcon = Euro;
  heartIcon = Heart;
  usersIcon = Users;
  treePineIcon = TreePine;
  dropletsIcon = Droplets;
  homeIcon = Home;
  briefcaseIcon = Briefcase;
  leafIcon = Leaf;
  shieldIcon = Shield;
  globeIcon = Globe;
  awardIcon = Award;
  xIcon = X;
  chevronLeftIcon = ChevronLeft;
  chevronRightIcon = ChevronRight;

  // Co-benefit icon mapping
  coBenefitIcons: Record<CoBenefit, LucideIconData> = {
    [CoBenefit.BIODIVERSITY]: Leaf,
    [CoBenefit.CLEAN_WATER]: Droplets,
    [CoBenefit.LOCAL_COMMUNITIES]: Users,
    [CoBenefit.EDUCATION]: Award,
    [CoBenefit.WOMEN_EMPOWERMENT]: Users,
    [CoBenefit.HEALTH]: Heart,
    [CoBenefit.FOREST_PROTECTION]: TreePine,
    [CoBenefit.LOCAL_JOBS]: Briefcase,
    [CoBenefit.RENEWABLE_ENERGY]: Home,
    [CoBenefit.POVERTY_REDUCTION]: Users,
    [CoBenefit.FOOD_SECURITY]: Leaf,
    [CoBenefit.CLIMATE_RESILIENCE]: Shield,
  };

  onClose(): void {
    this.visibleChange.emit(false);
    // Reset carousel to first image when closing
    this.currentImageIndex.set(0);
  }

  onSelect(): void {
    if (this.project) {
      this.selectProject.emit(this.project);
      this.onClose();
    }
  }

  // Carousel navigation
  nextImage(): void {
    const images = this.getProjectImages();
    if (images.length > 1) {
      this.currentImageIndex.update(i => (i + 1) % images.length);
      this.triggerSlide('right'); // New image comes from right
    }
  }

  prevImage(): void {
    const images = this.getProjectImages();
    if (images.length > 1) {
      this.currentImageIndex.update(i => (i - 1 + images.length) % images.length);
      this.triggerSlide('left'); // New image comes from left
    }
  }

  goToImage(index: number): void {
    const currentIndex = this.currentImageIndex();
    if (index !== currentIndex) {
      const direction = index > currentIndex ? 'right' : 'left';
      this.currentImageIndex.set(index);
      this.triggerSlide(direction);
    }
  }

  getProjectName(): string {
    if (!this.project) return '';
    const lang = this.transloco.getActiveLang().toUpperCase();
    if (lang === 'FR') return this.project.nameFR || this.project.nameEN;
    if (lang === 'NL') return this.project.nameNL || this.project.nameEN;
    return this.project.nameEN;
  }

  getShortDescription(): string {
    if (!this.project) return '';
    const lang = this.transloco.getActiveLang().toUpperCase();
    if (lang === 'FR') return this.project.shortDescriptionFR || this.project.shortDescriptionEN;
    if (lang === 'NL') return this.project.shortDescriptionNL || this.project.shortDescriptionEN;
    return this.project.shortDescriptionEN;
  }

  getLongDescription(): string {
    if (!this.project) return '';
    const lang = this.transloco.getActiveLang().toUpperCase();
    if (lang === 'FR') return this.project.longDescriptionFR || this.project.longDescriptionEN;
    if (lang === 'NL') return this.project.longDescriptionNL || this.project.longDescriptionEN;
    return this.project.longDescriptionEN;
  }

  getProjectTypeLabel(): string {
    if (!this.project) return '';
    const key = `contribute.projectTypes.${this.project.projectType}`;
    const translated = this.transloco.translate(key);
    return translated !== key ? translated : this.project.projectType.replace(/_/g, ' ');
  }

  getCertificationLabel(): string {
    if (!this.project) return '';
    const key = `contribute.certifications.${this.project.certificationStandard}`;
    const translated = this.transloco.translate(key);
    return translated !== key ? translated : this.project.certificationStandard.replace(/_/g, ' ');
  }

  getRegionLabel(): string {
    if (!this.project) return '';
    const key = `contribute.regions.${this.project.region}`;
    const translated = this.transloco.translate(key);
    return translated !== key ? translated : this.project.region.replace(/_/g, ' ');
  }

  getCoBenefitLabel(coBenefit: CoBenefit): string {
    const key = `contribute.coBenefits.${coBenefit}`;
    const translated = this.transloco.translate(key);
    return translated !== key ? translated : coBenefit.replace(/_/g, ' ');
  }

  getCoBenefitIcon(coBenefit: CoBenefit): LucideIconData {
    return this.coBenefitIcons[coBenefit] || Leaf;
  }

  getLocationDisplay(): string {
    if (!this.project) return '';
    const parts = [];
    if (this.project.location) parts.push(this.project.location);
    if (this.project.country) parts.push(this.project.country);
    if (parts.length === 0) return this.getRegionLabel();
    return parts.join(', ');
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(this.transloco.getActiveLang(), {
      month: 'long',
      year: 'numeric',
    });
  }

  formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString(this.transloco.getActiveLang());
  }

  hasImpactMetrics(): boolean {
    if (!this.project) return false;
    return !!(
      this.project.annualCO2Reduction ||
      this.project.annualBeneficiaries ||
      this.project.savedTreesPerTonne ||
      this.project.cookstovesPerTonne ||
      this.project.cleanWaterLitersPerTonne
    );
  }

  getImpactMetrics(): { icon: LucideIconData; value: string; label: string }[] {
    if (!this.project) return [];
    const metrics: { icon: LucideIconData; value: string; label: string }[] = [];

    if (this.project.annualCO2Reduction) {
      metrics.push({
        icon: Leaf,
        value: this.formatNumber(this.project.annualCO2Reduction),
        label: this.transloco.translate('contribute.projectDetails.metrics.annualCO2Reduction'),
      });
    }

    if (this.project.annualBeneficiaries) {
      metrics.push({
        icon: Users,
        value: this.formatNumber(this.project.annualBeneficiaries),
        label: this.transloco.translate('contribute.projectDetails.metrics.beneficiaries'),
      });
    }

    if (this.project.savedTreesPerTonne) {
      metrics.push({
        icon: TreePine,
        value: this.formatNumber(this.project.savedTreesPerTonne),
        label: this.transloco.translate('contribute.projectDetails.metrics.treesPerTonne'),
      });
    }

    if (this.project.cookstovesPerTonne) {
      metrics.push({
        icon: Home,
        value: this.formatNumber(this.project.cookstovesPerTonne),
        label: this.transloco.translate('contribute.projectDetails.metrics.cookstovesPerTonne'),
      });
    }

    if (this.project.cleanWaterLitersPerTonne) {
      metrics.push({
        icon: Droplets,
        value: this.formatNumber(this.project.cleanWaterLitersPerTonne),
        label: this.transloco.translate('contribute.projectDetails.metrics.cleanWaterPerTonne'),
      });
    }

    if (this.project.householdsSuppliedPerTonne) {
      metrics.push({
        icon: Home,
        value: this.formatNumber(this.project.householdsSuppliedPerTonne),
        label: this.transloco.translate('contribute.projectDetails.metrics.householdsPerTonne'),
      });
    }

    return metrics;
  }
}
